import React from 'react'
import type { PrepressJobSummary } from '@united-square/catalog-contract'
import { useAppStore } from '@runtime/core/store/appStore'
import { useI18n } from '@runtime/core/i18n/i18n'
import { Button } from '@runtime/components/ui/Button'
import {
  downloadPrepressArtifact,
  downloadPrepressResult,
  listPrepressJobs,
  PREPRESS_JOBS_CHANGED,
} from './prepressJobsApi'
import styles from './PrepressJobsPanel.module.scss'

export default function PrepressJobsPanel() {
  const token = useAppStore(state => state.authToken)
  const { t } = useI18n()
  const [jobs, setJobs] = React.useState<PrepressJobSummary[]>([])
  const [error, setError] = React.useState(false)
  React.useEffect(() => {
    if (!token) {
      setJobs([])
      return
    }
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    let loading = false
    let active = false
    const load = async () => {
      if (loading || controller.signal.aborted) return
      loading = true
      if (timer) clearTimeout(timer)
      try {
        const result = await listPrepressJobs(token, controller.signal)
        if (!controller.signal.aborted) {
          setJobs(result)
          setError(false)
        }
        active = result.some(job => ['queued', 'running'].includes(job.status))
      } catch {
        if (!controller.signal.aborted) setError(true)
      } finally {
        loading = false
        if (!controller.signal.aborted) timer = setTimeout(() => void load(), active ? 2000 : 15000)
      }
    }
    const refresh = () => {
      void load()
    }
    window.addEventListener(PREPRESS_JOBS_CHANGED, refresh)
    void load()
    return () => {
      controller.abort()
      if (timer) clearTimeout(timer)
      window.removeEventListener(PREPRESS_JOBS_CHANGED, refresh)
    }
  }, [token])

  if (!token || (!jobs.length && !error)) return null
  return (
    <section
      className={styles.panel}
      data-testid="prepress-jobs-panel"
      aria-label={t('hub.admin.prepressJobs.title')}
    >
      <details open={jobs.some(job => job.status === 'queued' || job.status === 'running')}>
        <summary>{t('hub.admin.prepressJobs.title')}</summary>
        {error ? <p role="alert">{t('hub.admin.prepressJobs.loadError')}</p> : null}
        <ul className={styles.jobs}>
          {jobs.map(job => (
            <li key={job.id} data-testid={`prepress-job-${job.id}`}>
              <p>{t(`hub.admin.prepressJobs.operations.${job.operation}`)}</p>
              <p role="status">{t(`hub.admin.prepressJobs.statuses.${job.status}`)}</p>
              {job.status === 'running' ? (
                <p>
                  {t(
                    `hub.admin.prepressJobs.stages.${['validating', 'rendering', 'normalizing', 'auditing', 'packaging', 'converting'].includes(job.stage) ? job.stage : 'processing'}`,
                  )}
                </p>
              ) : null}
              {job.errorCode && job.status === 'failed' ? (
                <p role="alert">
                  {t('hub.admin.prepressJobs.failedHint')}{' '}
                  {t('hub.admin.prepressJobs.errorCode', { code: job.errorCode })}
                </p>
              ) : null}
              {job.status === 'succeeded' ? (
                <>
                  <p>{t('hub.admin.prepressJobs.completedHint')}</p>
                  <div className={styles.actions}>
                    <Button
                      type="button"
                      onClick={() =>
                        void downloadPrepressResult(token, job).catch(() => setError(true))
                      }
                    >
                      {t('hub.admin.prepressJobs.downloadReport')}
                    </Button>
                    {job.artifact ? (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          void downloadPrepressArtifact(token, job).catch(() => setError(true))
                        }
                      >
                        {t('hub.admin.prepressJobs.download')}
                      </Button>
                    ) : null}
                  </div>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </details>
    </section>
  )
}
